import { t } from "@lingui/macro"
import {
    FormControl,
    Stack,
    Typography,
    FormControlLabel,
    Switch,
    Box,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback } from "react"

import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"
import { config_ConfigDefinitionItem } from "src/api"
import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"

interface IAgentInstancesStore {
    formFields: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        get: (...args: any[]) => unknown
    }
    agentDefinitions: config_ConfigDefinitionItem[]
    getSelectedAgentTypesForInstances(): config_ConfigDefinitionItem[]
    setSelectedInstancesForAgentType(
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ): void
    getSelectedInstancesForAgentType(
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[]
    getInstancesForAgentType(
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[]
    hasExistingInstanceConfigurations(): boolean
    // Method to deselect an agent type when required instances are removed
    deselectAgentType?(agentTypeId: number): void
}

interface IAgentInstancesProps {
    store: IAgentInstancesStore
}

export const AgentInstances = observer(({ store }: IAgentInstancesProps) => {
    // Get all selected agent types that can have instances
    const agentTypesForInstances = store.getSelectedAgentTypesForInstances()

    const handleInstanceChange = useCallback(
        (agentName: string) => (_event: unknown, value: unknown) => {
            const instances = Array.isArray(value) ? value : []
            store.setSelectedInstancesForAgentType(agentName, instances)

            // Find the agent definition for this agent name
            const agentDefinition = store.agentDefinitions.find(
                (def) => def.name === agentName,
            )

            if (agentDefinition != null) {
                const requiresInstances = Boolean(
                    (agentDefinition.metadata as Record<string, unknown>)
                        ?.agent_instance_required,
                )

                // If this agent type requires instances and no instances are selected,
                // deselect the agent type itself
                if (
                    requiresInstances &&
                    instances.length === 0 &&
                    store.deselectAgentType != null
                ) {
                    store.deselectAgentType(
                        agentDefinition.config_definition_id ?? 0,
                    )
                }
            }
        },
        [store],
    )

    const handleAgentTypeToggle = useCallback(
        (agentName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
            const isEnabled = event.target.checked

            if (isEnabled) {
                // Enable this agent type by setting a special marker value
                store.setSelectedInstancesForAgentType(agentName, [
                    { id: -1, label: "all-instances" },
                ])
            } else {
                // When disabled, clear selection
                store.setSelectedInstancesForAgentType(agentName, [])

                // Find the agent definition for this agent name
                const agentDefinition = store.agentDefinitions.find(
                    (def) => def.name === agentName,
                )

                if (agentDefinition != null) {
                    const requiresInstances = Boolean(
                        (agentDefinition.metadata as Record<string, unknown>)
                            ?.agent_instance_required,
                    )

                    // If this agent type requires instances and we're clearing all instances,
                    // deselect the agent type itself
                    if (requiresInstances && store.deselectAgentType != null) {
                        store.deselectAgentType(
                            agentDefinition.config_definition_id ?? 0,
                        )
                    }
                }
            }
        },
        [store],
    )

    const isAgentTypeEnabledForAll = useCallback(
        (agentName: string): boolean => {
            const selectedInstances =
                store.getSelectedInstancesForAgentType(agentName)
            return selectedInstances.some((instance) => instance.id === -1)
        },
        [store],
    )

    const getManuallySelectedInstances = useCallback(
        (agentName: string) => {
            const selectedInstances =
                store.getSelectedInstancesForAgentType(agentName)
            return selectedInstances.filter((instance) => instance.id !== -1)
        },
        [store],
    )

    // We no longer check for existing instance configurations for unselected types

    // Don't render anything if no agent types are selected
    if (agentTypesForInstances.length === 0) {
        return null
    }

    // Only show instances for currently selected agent types
    const getSelectedAgentTypesWithInstances = () => {
        // Only return the currently selected agent types that can have instances
        // No longer include legacy configurations for unselected types
        return agentTypesForInstances
    }

    const allAgentTypesToShow = getSelectedAgentTypesWithInstances()

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t`knowledge-item-modal.agent-configuration.title`}
            </Typography>
            {allAgentTypesToShow.map(
                (agentDefinition: config_ConfigDefinitionItem) => {
                    const agentName = agentDefinition.name
                    if (agentName == null || agentName.length === 0) return null

                    const requiresInstances = Boolean(
                        (agentDefinition.metadata as Record<string, unknown>)
                            ?.agent_instance_required,
                    )
                    const instances = store.getInstancesForAgentType(agentName)
                    const selectedInstances =
                        store.getSelectedInstancesForAgentType(agentName)

                    if (requiresInstances) {
                        // Show instance dropdown for agents that require specific instances
                        return (
                            <FormControl
                                key={agentName}
                                sx={{ breakInside: "avoid" }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ mb: 1, fontWeight: 500 }}
                                >
                                    {t({
                                        id: "knowledge-item-modal.agent-configuration.select-specific-instances",
                                        values: { agentName },
                                    })}
                                </Typography>
                                <AutoCompleteTextField
                                    multiple
                                    disableCloseOnSelect
                                    options={instances}
                                    value={selectedInstances}
                                    placeholder={t({
                                        id: "knowledge-item-modal.agent-configuration.select-instances-placeholder",
                                        values: { agentName },
                                    })}
                                    onChange={handleInstanceChange(agentName)}
                                    loading={instances.length === 0}
                                />
                            </FormControl>
                        )
                    } else {
                        // Show toggle + optional instance selection for agents that don't require specific instances
                        const isEnabledForAll =
                            isAgentTypeEnabledForAll(agentName)
                        const manuallySelected =
                            getManuallySelectedInstances(agentName)

                        return (
                            <Box key={agentName} sx={{ breakInside: "avoid" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isEnabledForAll}
                                            onChange={handleAgentTypeToggle(
                                                agentName,
                                            )}
                                        />
                                    }
                                    label={
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500 }}
                                            >
                                                {agentName}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                {t`knowledge-item-modal.agent-configuration.make-available-all-instances`}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                {!isEnabledForAll && (
                                    <Box sx={{ mt: 1, ml: 4 }}>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ mb: 1, display: "block" }}
                                        >
                                            {t`knowledge-item-modal.agent-configuration.or-select-specific-instances`}
                                        </Typography>
                                        <AutoCompleteTextField
                                            multiple
                                            disableCloseOnSelect
                                            options={instances}
                                            value={manuallySelected}
                                            placeholder={t({
                                                id: "knowledge-item-modal.agent-configuration.select-specific-instances-placeholder",
                                                values: { agentName },
                                            })}
                                            onChange={handleInstanceChange(
                                                agentName,
                                            )}
                                            loading={instances.length === 0}
                                            size="small"
                                        />
                                    </Box>
                                )}
                            </Box>
                        )
                    }
                },
            )}
        </Stack>
    )
})
